/**
 * MercureClient — manages Server-Sent Events connection to Mercure hub.
 */

type LogLevel = "info" | "success" | "warn" | "error";

export class MercureClient {
  private hubUrl: string;
  private toast: unknown;
  private eventSource: EventSource | null = null;
  private subscriptions = new Map<string, Set<(data: unknown) => void>>();
  private currentTopics = new Set<string>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isDev: boolean;
  private connectTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(hubUrl: string, toastInstance: unknown = null) {
    this.hubUrl = hubUrl;
    this.toast = toastInstance;
    this.isDev = import.meta.env.DEV;
  }

  log(message: string, data: unknown = null, type: LogLevel = "info") {
    if (!this.isDev) return;

    const prefix = `%c Mercure `;
    const baseStyle = "padding: 2px 4px; border-radius: 3px; font-weight: bold; color: white;";

    const styles: Record<LogLevel, string> = {
      info: `${baseStyle} background: #3498db;`,
      success: `${baseStyle} background: #2ecc71;`,
      warn: `${baseStyle} background: #f1c40f; color: #333;`,
      error: `${baseStyle} background: #e74c3c;`,
    };

    const args: unknown[] = [prefix, styles[type], message];
    if (data !== null) args.push(data);

    console[type === "success" || type === "info" ? "log" : type](...args);
  }

  connect() {
    if (this.connectTimeout) clearTimeout(this.connectTimeout);

    this.connectTimeout = setTimeout(() => {
      const newTopicList = Array.from(this.subscriptions.keys()).sort();

      if (newTopicList.length === 0) {
        if (this.eventSource) {
          this.log("Closing connection (no active subscriptions)");
          this.disconnect();
        }
        return;
      }

      const currentList = Array.from(this.currentTopics).sort();
      const hasChanged = JSON.stringify(newTopicList) !== JSON.stringify(currentList);

      if (this.eventSource?.readyState === EventSource.OPEN && !hasChanged) {
        return;
      }

      if (this.eventSource) {
        this.log(
          "Topic change detected, rotating connection...",
          {
            from: currentList,
            to: newTopicList,
          },
          "warn",
        );
        this.disconnect(false);
      }

      const url = new URL(this.hubUrl);
      newTopicList.forEach((topic) => url.searchParams.append("topic", topic));

      this.log("Connecting to Hub...", newTopicList);
      this.eventSource = new EventSource(url.toString(), { withCredentials: true });

      this.eventSource.onopen = () => {
        this.log("Connected", url.toString(), "success");
        this.reconnectAttempts = 0;
        this.currentTopics = new Set(newTopicList);
      };

      this.eventSource.onerror = () => {
        this.log("Connection Lost", null, "error");
        this.handleReconnect();
      };

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          let matched = false;

          const dataTopic =
            (data as Record<string, unknown>).topic || (data as Record<string, unknown>)["@id"];

          if (dataTopic) {
            this.subscriptions.forEach((callbacks, subscribedTopic) => {
              if (subscribedTopic === dataTopic || String(dataTopic).includes(subscribedTopic)) {
                callbacks.forEach((cb) => cb(data));
                matched = true;
              }
            });
          }

          if (!matched) {
            this.subscriptions.forEach((callbacks) => {
              callbacks.forEach((cb) => cb(data));
            });
          }
        } catch (err) {
          this.log("Parse Error", err, "error");
        }
      };
    }, 50);
  }

  disconnect(resetTopics = true) {
    if (this.eventSource) {
      this.log("Disconnecting...");
      this.eventSource.close();
      this.eventSource = null;
    }
    if (resetTopics) this.currentTopics.clear();
  }

  subscribe(topic: string, callback: (data: unknown) => void) {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }
    this.subscriptions.get(topic)!.add(callback);
    this.connect();
    return () => this.unsubscribe(topic, callback);
  }

  unsubscribe(topic: string, callback: (data: unknown) => void) {
    const callbacks = this.subscriptions.get(topic);
    if (callbacks) {
      callbacks.delete(callback);
      if (callbacks.size === 0) {
        this.subscriptions.delete(topic);
      }
    }
    this.connect();
  }

  /**
   * Force a connection rotation — closes the current EventSource and
   * reconnects with the current topic list. Used after permission
   * changes to re-authorize the SSE connection.
   */
  rotateConnection() {
    this.log("Force rotating connection (permission change)");
    if (this.eventSource) {
      this.disconnect(false);
    }
    this.reconnectAttempts = 0;
    this.connect();
  }

  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.log("Max reconnect attempts reached.", "", "error");
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * 2 ** (this.reconnectAttempts - 1);
    this.log(`Attempting reconnect #${this.reconnectAttempts} in ${delay}ms...`, "", "warn");

    setTimeout(() => this.connect(), delay);
  }
}

let mercureClient: MercureClient | null = null;

export function getMercureClient(
  hubUrl = import.meta.env.VITE_MERCURE_HUB_URL,
  toastInstance: unknown = null,
): MercureClient {
  if (!mercureClient) {
    mercureClient = new MercureClient(hubUrl, toastInstance);
  }
  return mercureClient;
}
